import { useState } from "react";
import Dialog from "components/Overlays/ResponsiveDialog";
import { Button } from "primereact/button";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import PhysicalBookFields from "../PhysicalBookFields";
import { FiPlus } from "react-icons/fi";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useGenres from "api/GenreResource/useGenres";
import { MAX_IMAGE_SIZE } from "utils";
import useAddPhysicalBook from "api/BookResource/useAddPhysicalBook";

const AddPhysicalBook = () => {
	const [visible, setVisible] = useState(false);

	// Fetch data for dropdowns
	const { data: categories, isLoading: isCategoriesLoading } =
		useFetchCategories("yes");
	const { data: genres, isLoading: isGenresLoading } = useGenres("yes");

	const initialValues = {
		title: "",
		author: "",
		isbn: "",
		description: "",
		category_id: "",
		genres: "",
		stock_level: "",
		physical_usd_price: "",
		book_cover: null,
	};

	const validationSchema = Yup.object().shape({
		title: Yup.string().required("Book title is required"),
		author: Yup.string().required("Author is required"),
		category_id: Yup.number().required("Category is required"),
		genres: Yup.string().required("Genre is required"),
		stock_level: Yup.number()
			.typeError("Stock level must be a number")
			.required("Stock level is required")
			.min(0, "Stock level must be 0 or greater"),
		physical_usd_price: Yup.number()
			.typeError("Price must be a number")
			.required("USD price is required")
			.min(0, "Price must be 0 or greater"),
		book_cover: Yup.mixed()
			.required("Book cover is required")
			.test({
				name: "fileSize",
				test: (value) => !value || value.size / 1024 / 1024 <= MAX_IMAGE_SIZE,
				message: `Image size should not exceed ${MAX_IMAGE_SIZE}MB`,
			})
			.test({
				name: "mimeType",
				test: (value) =>
					!value ||
					["image/jpeg", "image/png", "image/svg+xml", "image/webp"].includes(
						value.type
					),
				message:
					"Only images with these file extensions are allowed: jpeg, jpg, png, svg, webp",
			}),
	});

	// API hook for submitting book
	const { mutateAsync, isLoading } = useAddPhysicalBook();

	const handleSubmit = async (values, { resetForm }) => {
		try {
			await mutateAsync(values);
			setVisible(false);
			resetForm();
		} catch (error) {}
	};

	const categoryOptions =
		categories?.data?.map((category) => ({
			label: category.name,
			value: category.id,
		})) || [];

	const genreOptions =
		genres?.data?.data?.map((genre) => ({
			label: genre.name,
			value: genre.id,
		})) || [];

	return (
		<>
			<Button
				label="Add physical book"
				icon={<FiPlus />}
				onClick={() => setVisible(true)}
				className="portal-button portal-button-primary physical-book-add"
			/>

			<Dialog
				visible={visible}
				onHide={() => setVisible(false)}
				header="Add physical book"
				description="Add the print edition, cover, pricing and available stock."
				style={{ minWidth: 600, maxWidth: 600 }}
				modal
				className="p-fluid physical-book-dialog"
				blockScroll
				footer={
					<div className="flex justify-end gap-2">
						<Button
							label="Cancel"
							icon="pi pi-times"
							onClick={() => setVisible(false)}
							className="p-button-text"
						/>
						<Button
							label="Create Book"
							icon="pi pi-check"
							type="submit"
							form="physical-book-form"
							disabled={isLoading}
						/>
					</div>
				}
			>
				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
				>
					{({ values, errors, touched, setFieldValue }) => (
						<Form
							id="physical-book-form"
							className="akello-sectioned-form"
						>
							<PhysicalBookFields values={values} errors={errors} touched={touched} setFieldValue={setFieldValue}
								categoryOptions={categoryOptions} genreOptions={genreOptions}
								isCategoriesLoading={isCategoriesLoading} isGenresLoading={isGenresLoading} showStock />
						</Form>
					)}
				</Formik>
			</Dialog>
		</>
	);
};

export default AddPhysicalBook;
